import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Alert from '@mui/material/Alert';
import Sidebar from './Sidebar';
import { useUser } from '../contexts/UserContext';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Loading from './Loading';
import { UserProfile } from '../types/user';

const Profile = () => {
  const { profile, refresh } = useUser();
  const [editProfile, setEditProfile] = useState<UserProfile | null>(null);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    setEditProfile(profile);
  }, [profile]);

  if (!editProfile) {
    return <Loading />;
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditProfile({ ...editProfile, [e.target.name]: e.target.value });
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccess('');
    setError('');
    if (!editProfile?.id) {
      setError('User not loaded.');
      return;
    }
    const { error } = await supabase
      .from('profiles')
      .upsert({
        id: editProfile.id,
        name: editProfile.name,
        email: editProfile.email,
        role: editProfile.role,
        avatar_url: editProfile.avatar_url,
        is_premium: editProfile.is_premium,
        premium_until: editProfile.premium_until,
        updated_at: new Date().toISOString(),
      }, { onConflict: 'id' });
    if (error) setError(error.message);
    else {
      setSuccess('Profile updated successfully!');
      refresh();
    }
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !editProfile?.id) return;
    const file = e.target.files[0];
    const fileExt = file.name.split('.').pop();
    const filePath = `avatars/${editProfile.id}.${fileExt}`;
    setUploading(true);
    const { data: uploadData, error: uploadError } = await supabase.storage.from('avatars').upload(filePath, file, { upsert: true });
    if (uploadError) {
      setError('Failed to upload avatar.');
      setUploading(false);
      return;
    }
    const uploadedPath = uploadData?.path || filePath;
    const { data: publicUrlData } = supabase.storage.from('avatars').getPublicUrl(uploadedPath);
    if (publicUrlData?.publicUrl) {
      const avatarUrlToSave = publicUrlData.publicUrl;
      const { error: upsertError } = await supabase.from('profiles').upsert({
        id: editProfile.id,
        name: editProfile.name,
        email: editProfile.email,
        role: editProfile.role,
        avatar_url: avatarUrlToSave,
        is_premium: editProfile.is_premium,
        premium_until: editProfile.premium_until,
        updated_at: new Date().toISOString(),
      }, { onConflict: 'id' });
      if (upsertError) {
        setError('Failed to update avatar in profile: ' + upsertError.message);
      } else {
        refresh();
      }
    }
    setUploading(false);
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      <Sidebar user={editProfile} />
      <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Paper elevation={4} sx={{ p: 4, borderRadius: 3, minWidth: 360, maxWidth: 420 }}>
          <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
            <Box sx={{ position: 'relative' }}>
              <Avatar src={editProfile.avatar_url} sx={{ width: 80, height: 80, bgcolor: '#2563eb', fontWeight: 700, fontSize: 40 }}>
                {editProfile.name ? editProfile.name[0] : '?'}
              </Avatar>
              <IconButton
                color="primary"
                aria-label="upload picture"
                component="label"
                sx={{ position: 'absolute', bottom: 0, right: 0, bgcolor: 'white', boxShadow: 2 }}
                disabled={uploading}
              >
                <input hidden accept="image/*" type="file" onChange={handleAvatarChange} />
                {uploading ? <CircularProgress size={24} /> : <PhotoCamera />}
              </IconButton>
            </Box>
            <Typography variant="h5" fontWeight={700} mb={1}>
              Edit Profile
            </Typography>
            <form onSubmit={handleSave} style={{ width: '100%' }}>
              <Box display="flex" flexDirection="column" gap={2}>
                <TextField
                  name="name"
                  label="Name"
                  value={editProfile.name}
                  onChange={handleChange}
                  fullWidth
                />
                <TextField
                  name="email"
                  label="Email"
                  value={editProfile.email}
                  onChange={handleChange}
                  fullWidth
                  disabled
                />
                <TextField
                  name="role"
                  label="Role"
                  value={editProfile.role}
                  onChange={handleChange}
                  fullWidth
                  disabled
                />
                {editProfile.is_premium && (
                  <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                    Premium Member {editProfile.premium_until ? `until ${new Date(editProfile.premium_until).toLocaleDateString()}` : ''}
                  </Typography>
                )}
                {error && <Alert severity="error">{error}</Alert>}
                {success && <Alert severity="success">{success}</Alert>}
                <Button type="submit" variant="contained" fullWidth>
                  Save Changes
                </Button>
              </Box>
            </form>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default Profile; 